import { useState } from 'react'
import { salesPages } from '../../layout/sections'
import { Navigate } from 'react-router-dom'
import { PillNav } from '../../components/PillNav'
import { Badge, Button, ErrorBanner, TextInput } from '../../components/forms'
import { Permissions } from '../../auth/permissions'
import { useSession } from '../../auth/session'
import { formatDateTime, formatMoneyDocument, formatRecipient } from '../../lib/format'
import { creditNoteViewers } from '../../features/support/api'
import { channelLabels, type Channel, type EtimsStatus, type MessageItem, type MessageStatus , useRecordWhatsAppHandover, useWhatsAppHandover } from '../../features/messaging/api'

/** §6.6: the Sales pills — the till, the register, support, credit notes, and the messages sent about sales. */
export function SalesNav() {
  const can = useSession((s) => s.can)
  const items = salesPages(can)
  // The till is in the menu of its own (D-115); these are the desks behind it.
  return items.length > 1 ? <PillNav items={items} /> : null
}

/** Where "Sales" lands: the till for those who sell, the register for the office. */
export function SalesLanding({ till }: { till: React.ReactNode }) {
  const can = useSession((s) => s.can)
  if (can(Permissions.Sales.PosSell)) return <>{till}</>
  if (can(Permissions.Sales.ViewRegister)) return <Navigate to="/sales/register" replace />
  if (can(Permissions.Support.View)) return <Navigate to="/sales/support" replace />
  if (creditNoteViewers.some((p) => can(p))) return <Navigate to="/sales/credit-notes" replace />
  if (can(Permissions.Messaging.View)) return <Navigate to="/messages" replace />
  return <Navigate to="/" replace />
}

export function EtimsBadge({ status, sandbox }: { status?: EtimsStatus; sandbox?: boolean }) {
  if (!status) return <Badge>Not reported</Badge>
  if (status === 'Transmitted') return <Badge tone="success">{sandbox ? 'eTIMS (sandbox)' : 'eTIMS'}</Badge>
  if (status === 'Pending') return <Badge tone="warning">eTIMS pending</Badge>

  // Held rather than failed, and worth saying so: nothing is wrong, somebody just has to decide
  // what the missing VAT was before it can be filed (D-224).
  if (status === 'AwaitingClassification') return <Badge tone="info">Needs VAT basis</Badge>

  return <Badge tone="danger">eTIMS failed</Badge>
}

const statusTone: Record<MessageStatus, 'neutral' | 'info' | 'success' | 'danger'> = {
  Queued: 'neutral', Sent: 'info', Delivered: 'success', Failed: 'danger',
}

export function MessageStatusBadge({ status }: { status: MessageStatus }) {
  return <Badge tone={statusTone[status]}>{status}</Badge>
}

/** One line per message: what, to whom, where it has got to. */
export function MessageLine({ message: m }: { message: MessageItem }) {
  return (
    <li className="flex flex-wrap items-start justify-between gap-2 py-2 text-sm">
      <div className="min-w-0 break-words">
        <p>
          <span className="font-medium">{channelLabels[m.channel]}</span> to {formatRecipient(m.recipient)}
          {m.attachment !== 'None' && <span className="text-dpl-muted"> · with the PDF</span>}
        </p>
        <p className="text-xs text-dpl-muted">
          {formatDateTime(m.deliveredAt ?? m.sentAt ?? m.queuedAt)}
          {m.cost !== undefined && m.cost !== null && m.cost > 0 && ` · ${formatMoneyDocument(m.cost)}${m.costIsEstimate ? ' (est.)' : ''}`}
          {m.isSandbox && ' · sandbox'}
        </p>
        {m.failureReason && m.status !== 'Delivered' && <p className="text-xs text-dpl-danger">{m.failureReason}</p>}
      </div>
      <MessageStatusBadge status={m.status} />
    </li>
  )
}

/**
 * Sends a document on request. The recipient is filled from what is on record and can
 * be changed — the customer standing there gives the number or address they want.
 */
/**
 * Sends the receipt from the till's own WhatsApp (D-151): it opens WhatsApp with the message
 * already typed, and the cashier presses send. No business account needed, and no opt-in — a
 * person writing from their own phone is not the business messaging first.
 */
export function WhatsAppOnDevice({ invoiceId, phone }: { invoiceId: string; phone?: string }) {
  const [recipient, setRecipient] = useState(phone ?? '')
  const handover = useWhatsAppHandover(invoiceId, recipient, true)
  const record = useRecordWhatsAppHandover(invoiceId)

  return (
    <div className="rounded-md border border-dpl-border bg-dpl-bg/60 p-3">
      <p className="text-sm font-medium">Send it from this phone’s WhatsApp</p>
      <p className="mt-0.5 text-xs text-dpl-muted">
        Opens WhatsApp with the receipt link already typed. You press send, so it goes from your own number.
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <TextInput
          aria-label="WhatsApp number"
          inputMode="tel"
          placeholder="07… phone"
          value={recipient}
          onChange={(e) => setRecipient(e.target.value)}
          className="w-40"
        />
        <a
          href={handover.data?.url ?? '#'}
          target="_blank"
          rel="noreferrer noopener"
          aria-disabled={!handover.data}
          onClick={(e) => {
            if (!handover.data) {
              e.preventDefault()
              return
            }
            record.mutate(handover.data.phone)
          }}
          className={`inline-flex items-center rounded-md px-3 py-2 text-sm font-medium ${
            handover.data ? 'bg-dpl-success text-white hover:opacity-90' : 'pointer-events-none bg-dpl-border text-dpl-muted'
          }`}
        >
          Open WhatsApp
        </a>
        {record.isSuccess && <span role="status" className="text-sm text-dpl-success">Recorded as sent by hand.</span>}
      </div>
      <ErrorBanner error={handover.error ?? record.error} />
      {handover.data && (
        <p className="mt-2 whitespace-pre-line rounded border border-dpl-border bg-white p-2 text-xs text-dpl-muted">{handover.data.text}</p>
      )}
    </div>
  )
}

export function SendDocument({
  channels,
  defaults,
  busy,
  error,
  onSend,
}: {
  channels: Channel[]
  defaults: Partial<Record<Channel, string | undefined>>
  busy: boolean
  error: unknown
  onSend: (channel: Channel, recipient: string) => void
}) {
  const [channel, setChannel] = useState<Channel | null>(null)
  const [recipient, setRecipient] = useState('')

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {channels.map((c) => (
          <Button key={c} tone={channel === c ? 'primary' : 'secondary'} onClick={() => { setChannel(c); setRecipient(defaults[c] ?? '') }}>
            {c === 'Email' ? 'Email it' : `Send by ${channelLabels[c]}`}
          </Button>
        ))}
      </div>
      {channel && (
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            if (recipient.trim()) onSend(channel, recipient.trim())
          }}
        >
          <TextInput
            aria-label={channel === 'Email' ? 'Email address' : `${channelLabels[channel]} number`}
            className="min-w-0 flex-1 sm:w-64 sm:flex-none"
            inputMode={channel === 'Email' ? 'email' : 'tel'}
            placeholder={channel === 'Email' ? 'name@example.co.ke' : '07XX XXX XXX'}
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            autoFocus
          />
          <Button type="submit" busy={busy} disabled={!recipient.trim()}>Send</Button>
          {channel === 'WhatsApp' && (
            <p className="basis-full text-xs text-dpl-muted">Only when the customer asks for it: WhatsApp needs their agreement before a business writes to them.</p>
          )}
        </form>
      )}
      <ErrorBanner error={error} />
    </div>
  )
}
